import { PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js'
import BN from 'bn.js'
import { Pool, Donor, Proposal } from './anchorClient'

/**
 * ========================================================================
 *  Types for UI components - V3.1 Updated
 *  These interfaces define the shape of data expected by UI components
 *  V3.1 adds abort voting support
 * ========================================================================
 */

/**
 * Pool statistics displayed in PoolOverview component
 */
export interface PoolStats {
  totalValue: number           // Total pool value in SOL (converted from LST)
  totalPoolTokens: number      // V2: Total jitoSOL tokens held
  availablePoolTokens: number  // V2: Available (non-reserved) jitoSOL tokens
  totalContributors: number
  growthRate: number
  activeProposals: number
  successfulProposals: number
  averageContribution: number
  timeRemaining: number | undefined  // Time remaining in ms, undefined if no active proposal
  status: 'active' | 'pending' | 'completed' | 'failed'
  // V3.1: PumpSwap configuration status
  pumpswapEnabled: boolean
  pumpswapVersion: number
}

/**
 * Contributor data for DonorTable component
 * V3.1: Added strike tracking fields
 */
export interface UIContributor {
  id: string
  address: string
  amount: number               // SOL value of shares
  percentage: number
  timestamp: Date
  transactionHash: string
  isTopDonor: boolean
  totalDonations: number
  averageDonation: number
  firstDonationDate: Date
  status: 'active' | 'inactive'
  rank?: number
  // V3.1: Strike tracking for governance safety
  abortStrikeCount: number     // Strikes for voting abort on passing proposals
  proposeStrikeCount: number   // Strikes for having proposals aborted
}

/**
 * Proposal data for GovernancePanel component - V3.1 Updated
 * Now includes abort voting fields
 */
export interface UIProposal {
  id: string
  title: string                // V2: Now from on-chain data
  description: string          // V2: Now from on-chain data
  proposer: string             // V3.1: Proposer wallet address
  amount: number               // Requested SOL amount
  lockedPoolTokens: number     // V2: jitoSOL locked for this proposal
  recipient: string
  status: 'active' | 'passed' | 'rejected' | 'executed' | 'expired' | 'aborted'  // V3.1: Added 'aborted'
  votesFor: number
  votesAgainst: number
  totalVotes: number
  quorumRequired: number
  startTime: Date
  endTime: Date
  executionTime?: Date
  category: 'funding' | 'parameter' | 'upgrade' | 'treasury'
  priority: 'low' | 'medium' | 'high' | 'critical'
  tags: string[]
  // V3.1: Abort voting fields
  abortVoteCount: number       // Number of abort votes (0-2)
  abortVoter1: string          // First abort voter wallet (empty if none)
  abortVoter2: string          // Second abort voter wallet (empty if none)
}

/**
 * Voting power statistics for GovernancePanel component
 * V3.1: Added strike info
 */
export interface UIVotingPower {
  totalShares: number
  votingWeight: number
  delegatedVotes: number
  participationRate: number
  rank: number
  totalVotedProposals: number
  // V3.1: Strike tracking
  abortStrikeCount: number
  proposeStrikeCount: number
}

/**
 * V2: Exchange rate info for LST to SOL conversion
 */
export interface ExchangeRateInfo {
  lstToSol: number              // How many SOL per 1 LST (e.g., 1.05)
  solToLst: number              // How many LST per 1 SOL (e.g., 0.952)
  lastUpdated: Date
}

// Default exchange rate (jitoSOL typically trades at a premium)
const DEFAULT_EXCHANGE_RATE: ExchangeRateInfo = {
  lstToSol: 1.05,               // 1 jitoSOL ≈ 1.05 SOL
  solToLst: 0.952,              // 1 SOL ≈ 0.952 jitoSOL
  lastUpdated: new Date()
}

/**
 * ========================================================================
 *  Conversion Functions
 * ========================================================================
 */

/**
 * Convert lamports to SOL
 */
export function lamportsToSol(lamports: number | BN): number {
  const value = typeof lamports === 'number' ? lamports : lamports.toNumber()
  return value / LAMPORTS_PER_SOL
}

/**
 * Format SOL with 4 decimal places
 */
export function formatSol(lamports: number | BN): string {
  return lamportsToSol(lamports).toFixed(4)
}

/**
 * V2: Convert LST (jitoSOL) tokens to estimated SOL value
 * Uses the exchange rate to estimate SOL value
 */
export function lstToSolValue(
  lstTokens: number | BN, 
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): number {
  const lstAmount = typeof lstTokens === 'number' ? lstTokens : lstTokens.toNumber()
  const lstInUnits = lstAmount / LAMPORTS_PER_SOL
  return lstInUnits * exchangeRate.lstToSol
}

/**
 * V2: Format LST amount with SOL equivalent
 */
export function formatLstWithSol(
  lstTokens: number | BN,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): string {
  const lstAmount = typeof lstTokens === 'number' ? lstTokens : lstTokens.toNumber()
  const lstInUnits = lstAmount / LAMPORTS_PER_SOL
  const solValue = lstInUnits * exchangeRate.lstToSol
  return `${lstInUnits.toFixed(4)} jitoSOL (≈${solValue.toFixed(4)} SOL)`
}

/**
 * Calculate voting power percentage
 * Capped at 20% per wallet as per smart contract rules
 */
export function calculateVotingPower(donorAmount: number | BN, totalPool: number | BN): number {
  const amount = typeof donorAmount === 'number' ? donorAmount : donorAmount.toNumber()
  const total = typeof totalPool === 'number' ? totalPool : totalPool.toNumber()
  
  if (total === 0) return 0
  
  const rawPercentage = amount / total
  // Cap at 20% (0.20)
  return Math.min(rawPercentage, 0.20) * 100
}

/**
 * Calculate time remaining for proposal
 */
export function calculateTimeRemaining(endTime: number | BN): { hours: number, minutes: number, seconds: number, expired: boolean } {
  const end = typeof endTime === 'number' ? endTime : endTime.toNumber() * 1000 // Convert to ms if timestamp
  const now = Date.now()
  const diff = end - now
  
  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, expired: true }
  }
  
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)
  
  return { hours, minutes, seconds, expired: false }
}

/**
 * Check proposal creation eligibility
 * Requirements:
 * 1. Must have at least 1 SOL deposited
 * 2. Must have held shares for at least 24 hours (approx 216,000 slots)
 * V3.1: Also checks for strike count threshold
 */
export function canCreateProposal(
  donor: Donor | null, 
  currentSlot: number = 0, // Pass 0 if slot not available, will skip slot check or fail safe
  maxStrikes: number = 3   // V3.1: Maximum allowed propose strikes
): { eligible: boolean, reason: string } {
  if (!donor) {
    return { eligible: false, reason: 'You must be a donor to create a proposal' }
  }
  
  // V3.1: Check strike count
  if (donor.proposeStrikeCount >= maxStrikes) {
    return { eligible: false, reason: `You have ${donor.proposeStrikeCount} strikes and cannot create proposals` }
  }
  
  // Check 1 SOL minimum (1_000_000_000 lamports)
  const MIN_PROPOSAL_DEPOSIT = 1_000_000_000
  if (donor.shares.toNumber() < MIN_PROPOSAL_DEPOSIT) {
    return { eligible: false, reason: 'You need at least 1 SOL equivalent in the pool to create a proposal' }
  }
  
  // Check 24h holding period (216,000 slots)
  // If currentSlot is 0 (not provided), we might skip this check or assume eligible for UI
  // In a real app, we'd want the actual current slot
  if (currentSlot > 0) {
    const MIN_HOLDING_SLOTS = 216_000
    const slotsHeld = currentSlot - donor.lastSharesChangeSlot.toNumber()
    if (slotsHeld < MIN_HOLDING_SLOTS) {
      return { eligible: false, reason: 'You must hold shares for at least 24 hours before creating a proposal' }
    }
  }
  
  return { eligible: true, reason: '' }
}

/**
 * V3.1: Check if user can vote abort on a proposal
 * Requirements:
 * 1. Must be a donor
 * 2. Must not already be an abort voter on this proposal
 * 3. Must not have max abort strikes
 */
export function canVoteAbort(
  donor: Donor | null,
  proposal: Proposal,
  userWallet: string,
  maxStrikes: number = 3
): { eligible: boolean, reason: string } {
  if (!donor) {
    return { eligible: false, reason: 'You must be a donor to vote abort' }
  }
  
  // Check if already an abort voter
  const isAborter1 = proposal.abortVoter1.toBase58() === userWallet
  const isAborter2 = proposal.abortVoter2.toBase58() === userWallet
  
  if (isAborter1 || isAborter2) {
    return { eligible: false, reason: 'You have already voted to abort this proposal' }
  }
  
  // Check if proposal already has 2 abort votes
  if (proposal.abortCount >= 2) {
    return { eligible: false, reason: 'This proposal already has maximum abort votes' }
  }
  
  // Check strike count - high strike count means risky to vote abort
  if (donor.abortStrikeCount >= maxStrikes) {
    return { eligible: false, reason: `You have ${donor.abortStrikeCount} abort strikes and cannot vote abort` }
  }
  
  return { eligible: true, reason: '' }
}

/**
 * Calculate withdrawable amount (not locked in proposals)
 * V2: Considers shares and reserved pool tokens
 */
export function getWithdrawableAmount(
  donor: Donor | null,
  pool: Pool | null,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): number {
  if (!donor || !pool) return 0
  
  // Calculate donor's share of the pool
  const totalShares = pool.totalShares.toNumber()
  const donorShares = donor.shares.toNumber()
  
  if (totalShares === 0) return 0
  
  // Calculate donor's proportion of the pool
  const proportion = donorShares / totalShares
  
  // V2: Available pool tokens (total - reserved)
  const availablePoolTokens = pool.totalPoolTokens.toNumber() - pool.reservedPoolTokens.toNumber()
  
  // Donor's share of available tokens
  const donorPoolTokens = availablePoolTokens * proportion
  
  // Convert to SOL value
  return lstToSolValue(donorPoolTokens, exchangeRate)
}

/**
 * ========================================================================
 *  V3.1 Transform Functions - Pool Data to UI Components
 * ========================================================================
 */

/**
 * Transform V3.1 Pool data for PoolOverview component
 * Converts LST-based accounting to SOL-equivalent values
 * Now includes PumpSwap configuration status
 */
export function transformPoolForOverview(
  pool: Pool, 
  donorsCount: number, 
  activeProposal: Proposal | null,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): PoolStats {
  // Calculate growth rate (mock for now as we don't have historical data)
  const growthRate = 0 
  
  // Determine status
  let status: PoolStats['status'] = 'active'
  if (activeProposal) {
    status = 'active'
  } else {
    status = 'completed' // Or 'pending' if we had that state
  }
  
  // V2: Calculate values from LST tokens
  const totalPoolTokens = pool.totalPoolTokens.toNumber()
  const reservedPoolTokens = pool.reservedPoolTokens.toNumber()
  const availablePoolTokens = totalPoolTokens - reservedPoolTokens
  
  // Convert to SOL value for display
  const totalValue = lstToSolValue(totalPoolTokens, exchangeRate)
  
  return {
    totalValue,
    totalPoolTokens: totalPoolTokens / LAMPORTS_PER_SOL,
    availablePoolTokens: availablePoolTokens / LAMPORTS_PER_SOL,
    totalContributors: donorsCount,
    growthRate,
    activeProposals: activeProposal ? 1 : 0,
    successfulProposals: pool.proposalCount.toNumber(), // Using total count as proxy for now
    averageContribution: donorsCount > 0 ? totalValue / donorsCount : 0,
    timeRemaining: activeProposal ? (activeProposal.deadlineTs.toNumber() * 1000) - Date.now() : undefined,
    status,
    // V3.1: PumpSwap configuration
    pumpswapEnabled: pool.pumpswapEnabled,
    pumpswapVersion: pool.pumpswapVersion
  }
}

/**
 * Transform V3.1 Donor data for DonorTable component
 * Now includes strike tracking fields
 */
export function transformDonorsForTable(
  donors: Array<{ account: Donor; publicKey: PublicKey }>,
  pool: Pool,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): UIContributor[] {
  const totalShares = pool.totalShares.toNumber()
  const totalPoolTokens = pool.totalPoolTokens.toNumber()
  
  return donors.map((item, index) => {
    const donor = item.account
    const shares = donor.shares.toNumber()
    const percentage = totalShares > 0 ? (shares / totalShares) * 100 : 0
    
    // V2: Calculate SOL value from donor's share of pool tokens
    const proportion = totalShares > 0 ? shares / totalShares : 0
    const donorPoolTokens = totalPoolTokens * proportion
    const solValue = lstToSolValue(donorPoolTokens, exchangeRate)
    
    // Mock data for fields not on-chain
    // In a real indexer solution, these would come from transaction history
    const mockTimestamp = new Date(Date.now() - (Math.random() * 30 * 24 * 60 * 60 * 1000))
    
    return {
      id: item.publicKey.toBase58(),
      address: donor.wallet.toBase58(),
      amount: solValue,
      percentage,
      timestamp: mockTimestamp,
      transactionHash: '...', // Would need indexer
      isTopDonor: index < 3, // Simple logic: first 3 are top
      totalDonations: 1, // Would need indexer
      averageDonation: solValue,
      firstDonationDate: mockTimestamp,
      status: 'active' as const,
      rank: index + 1,
      // V3.1: Strike tracking
      abortStrikeCount: donor.abortStrikeCount,
      proposeStrikeCount: donor.proposeStrikeCount
    }
  }).sort((a, b) => b.amount - a.amount) // Sort by amount descending
}

/**
 * Transform V3.1 Proposal data for GovernancePanel component
 * Now includes abort voting support
 *
 * Status meanings (Updated for V3.1):
 * - 'executed': Proposal has been executed on-chain (funds transferred or unlocked)
 * - 'rejected': Proposal was finalized as failed on-chain
 * - 'aborted': V3.1 - Proposal was aborted by 2 eligible wallets
 * - 'passed': Voting period ended, READY FOR EXECUTION (needs execute call to finalize)
 *            This applies whether the vote passed OR failed - execution unlocks funds either way
 * - 'active': Voting is still in progress
 * - 'expired': Not used currently - we use 'passed' to indicate ready for execution
 */
export function transformProposalForGovernance(
  proposal: Proposal,
  pool: Pool,
  proposalPubkey: PublicKey,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): UIProposal {
  const now = Date.now()
  const endTime = proposal.deadlineTs.toNumber() * 1000
  const isVotingEnded = now > endTime
  
  // Determine status - Check on-chain status first (already finalized states)
  // The IDL shows status as an enum/object: { active: {} } | { failed: {} } | { executed: {} } | { aborted: {} }
  let status: UIProposal['status'] = 'active'
  
  if ('executed' in proposal.status) {
    status = 'executed'
  } else if ('failed' in proposal.status) {
    status = 'rejected'
  } else if ('aborted' in proposal.status) {
    // V3.1: Handle aborted status
    status = 'aborted'
  } else if (isVotingEnded) {
    // Voting period has ended but proposal not yet executed on-chain
    // Set to 'passed' to trigger Execute button display
    // The execute function will determine pass/fail outcome and handle funds accordingly
    status = 'passed'
  } else {
    // Voting still in progress
    status = 'active'
  }
  
  // Calculate quorum required in raw votes (lamports)
  // quorumBps is basis points (e.g. 3000 = 30%)
  // totalSnapshotShares is the total shares at snapshot
  const quorumRequired = (proposal.totalSnapshotShares.toNumber() * pool.quorumBps) / 10000
  
  // V2: Calculate locked pool tokens value in SOL
  const lockedLstValue = lstToSolValue(proposal.lockedPoolTokens, exchangeRate)
  
  // V3.1: Use proposerWallet from on-chain data
  const proposer = proposal.proposerWallet.toBase58()
  
  // V2: Use title and description from on-chain data
  const title = proposal.title || `Funding Request: ${formatSol(proposal.requestedLamports)} SOL`
  const description = proposal.description || `Proposal to send ${formatSol(proposal.requestedLamports)} SOL to ${proposal.destinationWallet.toBase58()}`
  
  // V3.1: Get abort voter addresses (convert PublicKey.default to empty string)
  const defaultPubkey = PublicKey.default.toBase58()
  const abortVoter1 = proposal.abortVoter1.toBase58()
  const abortVoter2 = proposal.abortVoter2.toBase58()
  
  return {
    id: proposalPubkey.toBase58(),
    title,
    description,
    proposer,
    amount: lamportsToSol(proposal.requestedLamports),
    lockedPoolTokens: lockedLstValue,
    recipient: proposal.destinationWallet.toBase58(),
    status,
    votesFor: lamportsToSol(proposal.yesWeight),
    votesAgainst: lamportsToSol(proposal.noWeight),
    totalVotes: lamportsToSol(proposal.participationWeight),
    quorumRequired: lamportsToSol(new BN(quorumRequired)),
    startTime: new Date(proposal.createdAtTs.toNumber() * 1000),
    endTime: new Date(endTime),
    category: 'funding',
    priority: 'medium',
    tags: ['Funding', 'Treasury'],
    // V3.1: Abort voting fields
    abortVoteCount: proposal.abortCount,
    abortVoter1: abortVoter1 === defaultPubkey ? '' : abortVoter1,
    abortVoter2: abortVoter2 === defaultPubkey ? '' : abortVoter2
  }
}

/**
 * Calculate user's voting power stats for GovernancePanel
 * V3.1: Now includes strike tracking
 */
export function calculateUserVotingPower(
  userDonor: Donor | null,
  pool: Pool,
  allDonors: Array<{ account: Donor; publicKey: PublicKey }>,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): UIVotingPower {
  if (!userDonor) {
    return {
      totalShares: 0,
      votingWeight: 0,
      delegatedVotes: 0,
      participationRate: 0,
      rank: 0,
      totalVotedProposals: 0,
      // V3.1: Default strike values
      abortStrikeCount: 0,
      proposeStrikeCount: 0
    }
  }
  
  const totalShares = pool.totalShares.toNumber()
  const userShares = userDonor.shares.toNumber()
  
  // Calculate rank
  const sortedDonors = [...allDonors].sort((a, b) => b.account.shares.toNumber() - a.account.shares.toNumber())
  const rank = sortedDonors.findIndex(d => d.account.wallet.toBase58() === userDonor.wallet.toBase58()) + 1
  
  // Calculate voting weight (capped at 20%)
  const rawWeight = totalShares > 0 ? userShares / totalShares : 0
  const votingWeight = Math.min(rawWeight, 0.20)
  
  // V2: Calculate SOL value from user's share of pool tokens
  const totalPoolTokens = pool.totalPoolTokens.toNumber()
  const proportion = totalShares > 0 ? userShares / totalShares : 0
  const userPoolTokens = totalPoolTokens * proportion
  const userSolValue = lstToSolValue(userPoolTokens, exchangeRate)
  
  return {
    totalShares: userSolValue, // Display as SOL value instead of raw shares
    votingWeight,
    delegatedVotes: 0,
    participationRate: 1.0, // Mock value
    rank: rank > 0 ? rank : allDonors.length + 1,
    totalVotedProposals: 0, // Would need to fetch all vote records
    // V3.1: Strike tracking from donor account
    abortStrikeCount: userDonor.abortStrikeCount,
    proposeStrikeCount: userDonor.proposeStrikeCount
  }
}

/**
 * ========================================================================
 *  V3.1 Helper Functions for LST Display
 * ========================================================================
 */

/**
 * V2: Calculate the total pool value in SOL
 * Takes into account the LST-to-SOL exchange rate
 */
export function calculatePoolSolValue(
  pool: Pool,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): {
  totalSolValue: number
  availableSolValue: number
  reservedSolValue: number
} {
  const totalPoolTokens = pool.totalPoolTokens.toNumber()
  const reservedPoolTokens = pool.reservedPoolTokens.toNumber()
  const availablePoolTokens = totalPoolTokens - reservedPoolTokens
  
  return {
    totalSolValue: lstToSolValue(totalPoolTokens, exchangeRate),
    availableSolValue: lstToSolValue(availablePoolTokens, exchangeRate),
    reservedSolValue: lstToSolValue(reservedPoolTokens, exchangeRate)
  }
}

/**
 * V2: Calculate a donor's portion of the pool
 * V3.1: Added strike tracking info
 */
export function calculateDonorPoolPortion(
  donor: Donor,
  pool: Pool,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): {
  percentage: number
  lstTokens: number
  solValue: number
  withdrawableSolValue: number
  abortStrikeCount: number
  proposeStrikeCount: number
} {
  const totalShares = pool.totalShares.toNumber()
  const donorShares = donor.shares.toNumber()
  
  if (totalShares === 0) {
    return {
      percentage: 0,
      lstTokens: 0,
      solValue: 0,
      withdrawableSolValue: 0,
      abortStrikeCount: donor.abortStrikeCount,
      proposeStrikeCount: donor.proposeStrikeCount
    }
  }
  
  const percentage = (donorShares / totalShares) * 100
  const proportion = donorShares / totalShares
  
  // Total tokens donor owns
  const donorLstTokens = pool.totalPoolTokens.toNumber() * proportion
  const donorSolValue = lstToSolValue(donorLstTokens, exchangeRate)
  
  // Withdrawable (not reserved)
  const availablePoolTokens = pool.totalPoolTokens.toNumber() - pool.reservedPoolTokens.toNumber()
  const donorAvailableLstTokens = availablePoolTokens * proportion
  const withdrawableSolValue = lstToSolValue(donorAvailableLstTokens, exchangeRate)
  
  return {
    percentage,
    lstTokens: donorLstTokens / LAMPORTS_PER_SOL,
    solValue: donorSolValue,
    withdrawableSolValue,
    // V3.1: Strike tracking
    abortStrikeCount: donor.abortStrikeCount,
    proposeStrikeCount: donor.proposeStrikeCount
  }
}

/**
 * V2: Format pool stats for display
 */
export function formatPoolStats(
  pool: Pool,
  donorsCount: number,
  exchangeRate: ExchangeRateInfo = DEFAULT_EXCHANGE_RATE
): {
  totalValueFormatted: string
  lstTokensFormatted: string
  donorsCountFormatted: string
  averageContributionFormatted: string
} {
  const { totalSolValue } = calculatePoolSolValue(pool, exchangeRate)
  const lstTokens = pool.totalPoolTokens.toNumber() / LAMPORTS_PER_SOL
  const avgContribution = donorsCount > 0 ? totalSolValue / donorsCount : 0
  
  return {
    totalValueFormatted: `${totalSolValue.toFixed(2)} SOL`,
    lstTokensFormatted: `${lstTokens.toFixed(4)} jitoSOL`,
    donorsCountFormatted: donorsCount.toString(),
    averageContributionFormatted: `${avgContribution.toFixed(4)} SOL`
  }
}

/**
 * V2: Get staking yield estimate
 * jitoSOL earns staking rewards, so the pool grows over time
 */
export function getEstimatedYield(
  pool: Pool,
  annualYieldPercent: number = 7.0 // Approximate Solana staking yield
): {
  dailyYieldSol: number
  weeklyYieldSol: number
  monthlyYieldSol: number
  annualYieldSol: number
} {
  const totalPoolTokens = pool.totalPoolTokens.toNumber() / LAMPORTS_PER_SOL
  const dailyRate = annualYieldPercent / 100 / 365
  
  return {
    dailyYieldSol: totalPoolTokens * dailyRate,
    weeklyYieldSol: totalPoolTokens * dailyRate * 7,
    monthlyYieldSol: totalPoolTokens * dailyRate * 30,
    annualYieldSol: totalPoolTokens * (annualYieldPercent / 100)
  }
}

/**
 * V3.1: Format abort vote status for display
 * Returns human-readable information about the abort vote state
 */
export function formatAbortVoteStatus(proposal: UIProposal): {
  hasAbortVotes: boolean
  abortVoteText: string
  canBeAborted: boolean
  abortWarning: string | null
} {
  const hasAbortVotes = proposal.abortVoteCount > 0
  const canBeAborted = proposal.abortVoteCount >= 2
  
  let abortVoteText = 'No abort votes'
  if (proposal.abortVoteCount === 1) {
    abortVoteText = '1 abort vote (needs 2 to abort)'
  } else if (proposal.abortVoteCount >= 2) {
    abortVoteText = '2 abort votes - Can be aborted!'
  }
  
  // Warning about risk of voting abort
  let abortWarning: string | null = null
  if (proposal.status === 'active' && hasAbortVotes) {
    abortWarning = 'Warning: If this proposal passes and was aborted, abort voters receive a strike.'
  }
  
  return {
    hasAbortVotes,
    abortVoteText,
    canBeAborted,
    abortWarning
  }
}

/**
 * V3.1: Check if user has voted abort on a proposal
 */
export function hasUserVotedAbort(proposal: UIProposal, userWallet: string): boolean {
  if (!userWallet) return false
  return proposal.abortVoter1 === userWallet || proposal.abortVoter2 === userWallet
}

/**
 * V3.1: Format strike count with warning level
 */
export function formatStrikeCount(strikeCount: number, maxStrikes: number = 3): {
  text: string
  level: 'safe' | 'warning' | 'danger'
  canParticipate: boolean
} {
  const canParticipate = strikeCount < maxStrikes
  
  let level: 'safe' | 'warning' | 'danger' = 'safe'
  if (strikeCount >= maxStrikes) {
    level = 'danger'
  } else if (strikeCount >= maxStrikes - 1) {
    level = 'warning'
  }
  
  const text = `${strikeCount}/${maxStrikes} strikes`
  
  return {
    text,
    level,
    canParticipate
  }
}
